/**
 * CodeEditor Component
 * 
 * Monaco Editor wrapper configured for viewing cloud code.
 * Features:
 * - VS Code Dark theme
 * - Read-only mode by default
 * - Auto language detection
 * - Glassmorphism-compatible styling
 */

import Editor from '@monaco-editor/react';

interface CodeEditorProps {
  /** Code content to display */
  code: string;
  /** Programming language for syntax highlighting */
  language: string;
  /** Whether the editor is read-only (default: true) */
  readOnly?: boolean;
  /** Whether content is loading */
  isLoading?: boolean;
}

/**
 * Get Monaco language identifier from file extension
 */
export function getLanguageFromExtension(filename: string): string {
  const ext = filename.split('.').pop()?.toLowerCase() || '';
  
  const languageMap: Record<string, string> = {
    js: 'javascript',
    mjs: 'javascript',
    jsx: 'javascript',
    ts: 'typescript',
    tsx: 'typescript',
    json: 'json',
    html: 'html',
    htm: 'html',
    css: 'css',
    scss: 'scss',
    less: 'less',
    md: 'markdown',
    yaml: 'yaml',
    yml: 'yaml',
    xml: 'xml',
    sh: 'shell',
    bash: 'shell',
    txt: 'plaintext',
  };

  return languageMap[ext] || 'plaintext';
}

export default function CodeEditor({
  code,
  language,
  readOnly = true,
  isLoading = false,
}: CodeEditorProps) {
  // Loading state with skeleton
  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center bg-[#1e1e1e]">
        <div className="flex items-center gap-3 text-white/50">
          <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
            <circle 
              className="opacity-25" 
              cx="12" 
              cy="12" 
              r="10" 
              stroke="currentColor" 
              strokeWidth="4" 
              fill="none" 
            />
            <path 
              className="opacity-75" 
              fill="currentColor" 
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" 
            />
          </svg>
          <span>Loading file...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-hidden bg-[#1e1e1e]">
      <Editor
        height="100%"
        language={language}
        value={code}
        theme="vs-dark"
        options={{
          readOnly,
          minimap: { enabled: true, scale: 0.8 },
          fontSize: 13,
          fontFamily: "'JetBrains Mono', 'Fira Code', 'Cascadia Code', Consolas, monospace",
          fontLigatures: true,
          lineNumbers: 'on',
          scrollBeyondLastLine: false,
          automaticLayout: true,
          padding: { top: 16, bottom: 16 },
          smoothScrolling: true,
          cursorBlinking: 'smooth',
          cursorSmoothCaretAnimation: 'on',
          renderLineHighlight: 'line',
          scrollbar: {
            verticalScrollbarSize: 10,
            horizontalScrollbarSize: 10,
            useShadows: false,
          },
          overviewRulerBorder: false,
          hideCursorInOverviewRuler: true,
          // Disable editing features since it's read-only
          quickSuggestions: false,
          suggestOnTriggerCharacters: false,
          acceptSuggestionOnEnter: 'off',
          tabCompletion: 'off',
          wordBasedSuggestions: 'off',
          parameterHints: { enabled: false },
          folding: true,
          foldingHighlight: true,
          showFoldingControls: 'mouseover',
          bracketPairColorization: { enabled: true },
          guides: {
            bracketPairs: true,
            indentation: true,
          },
        }}
        loading={
          <div className="flex items-center justify-center h-full bg-[#1e1e1e] text-white/50">
            <span>Initializing editor...</span>
          </div>
        }
      />
    </div>
  );
}

